import { z } from 'zod';
import { PointSchema } from './geojson';
import { IncidentLifecycleStateSchema } from './incident';

/**
 * High-risk hotspot feature (Phase F.1.1) — the fusion engine output served by
 * GET /api/high-risk-hotspots. Backed by the `high_risk_hotspots` table written
 * by tsr-fusion-worker, which correlates the E.1 ingest layer (satellite
 * hotspots, FWI, AQHI, PurpleAir PM2.5, NOAA HMS smoke, active fires) and scores
 * each candidate hotspot 0–100.
 *
 * BACKGROUND ONLY at this stage: nothing in apps/web renders this yet. The
 * render is E.2.3 and the click-to-confirm-dispatch UX is R.3, both of which
 * consume this contract. See docs/phase-f1-scope/01-fusion-logic-design.md.
 *
 * Two shapes: the LIST endpoint returns a compact signal summary; the DETAIL
 * endpoint (`/:id`) returns the full corroborating_signals payload.
 */

/** Compact, non-PII signal summary returned by the list endpoint. */
export const HighRiskSignalSummarySchema = z.object({
  satellite_corroboration: z.boolean(),
  fwi: z.number().nullable(),
  hms_overlap: z.boolean(),
  active_fire_nearby: z.boolean(),
  new_area_boost: z.boolean(),
  aqhi_nearby_count: z.number().int().nonnegative(),
  purpleair_spike_count: z.number().int().nonnegative(),
  // #442 Track A Slice 2 — the cluster was DEMOTED off urgent by the static-heat mask
  // (a documented industrial heat source, not a fire). Lets the muted Fusion-Candidates
  // layer/queue label the card without a detail fetch. Optional (back-compat with
  // pre-existing detail-constructed features + fixtures); read with `?? false`.
  static_heat_masked: z.boolean().optional(),
  // #447 Track A Slice 3b — compact demotion verdict so the queue card / muted layer can
  // label "demoted · known fire <name>" without a detail fetch (all optional, back-compat).
  demoted: z.boolean().optional(),
  demoted_reason: z.enum(['static_heat', 'connected_fire']).nullable().optional(),
  connected_fire_id: z.string().nullable().optional(),
  connected_fire_name: z.string().nullable().optional(),
  persistence_looks: z.number().nullable().optional(),
  // #448 Track A Slice 3c — the values-at-risk VETO kept this genuine, unconnected hotspot
  // urgent because a NATIONAL populated place sits within range. Compact fields so the queue
  // card can label "kept urgent — N km from <place>" (+ the downwind evacuation-risk flag)
  // without a detail fetch. The queue/SQL admits a values_kept_urgent row past the confidence
  // floor (§G M1). All optional (back-compat); read with `?? false`/`?? null`.
  values_kept_urgent: z.boolean().optional(),
  values_place_name: z.string().nullable().optional(),
  values_place_kind: z.string().nullable().optional(),
  values_place_distance_m: z.number().nullable().optional(),
  values_place_bearing_deg: z.number().nullable().optional(),
  values_place_downwind: z.boolean().optional(),
});
export type HighRiskSignalSummary = z.infer<typeof HighRiskSignalSummarySchema>;

/** The primary (winning) raw hotspot summary carried on every feature. */
export const PrimaryHotspotSchema = z.object({
  source: z.string(),
  frp: z.number().nullable(),
});

/**
 * Coordination re-arch Phase 1 — the LIGHT "owned · incident #N" read.
 *
 * When an operator has Declared/owned this hotspot, the API LEFT-JOINs the tracked
 * incident (incidents.source_record_type='high_risk_hotspot' AND source_record_id =
 * hotspot.id — the 1:1 idempotent provenance link) and carries this thin reference
 * onto the feature. Lets the alert detail (and, in Phase 5, the map glyph) read
 * "owned · incident #N" without a second fetch. Absent/null when not yet owned.
 */
/**
 * One participant in the (possibly unified) incident command — a Lead Organization + its
 * command officer. Carried on the owned-incident ref so a NON-OWNER can read the full command
 * roster of a foreign-owned hazard (Unified Command established — <A> · <B>, §4.7) without a
 * second, RLS-gated fetch. Sourced from the incident_common_picture view's co_leads jsonb.
 */
export const OwnedIncidentCoLeadSchema = z.object({
  agency_name: z.string(),
  agency_kind: z.string(),
  is_lead: z.boolean(),
  functional_role: z.string().nullable().optional(),
  officer_name: z.string().nullable().optional(),
});
export type OwnedIncidentCoLead = z.infer<typeof OwnedIncidentCoLeadSchema>;

export const OwnedIncidentRefSchema = z.object({
  id: z.string(),
  incident_number: z.string(),
  status: z.enum(['open', 'closed']),
  /**
   * ── Ownership-coordination block (migration 0151 `incident_common_picture` view). ──
   *
   * COMMAND is exclusive; the PICTURE is shared. These fields let EVERY agency — including a
   * non-participant neighbour — read WHO holds Incident Command of this hazard and how to reach
   * them, from the SAME thin ref, across every hazard family. All optional/nullable so
   * detail-constructed + pre-existing-test features stay valid; present when the hazard carries
   * a LIVE command lead (owned/response lifecycle). Attribution = Lead Organization + Incident
   * Commander; CONTACT routes through the Liaison Officer (App A #46 — the outside-contact for
   * requests for assistance), falling back to the Incident Commander when no LO is stood up.
   */
  /** Lead Organization (§4.5) — read from the LIVE command lead, NOT the creating tenant. */
  lead_organization_name: z.string().nullable().optional(),
  /** Jurisdiction tier — township vs province vs band council determines what they can COMMIT. */
  lead_organization_kind: z.string().nullable().optional(),
  /** Incident Commander (App A #37) — accountability, not the contact door. */
  incident_commander_name: z.string().nullable().optional(),
  /** When the IC assumed command (ISO) — accountability AND freshness ("since 04:12"). */
  command_assumed_at: z.string().nullable().optional(),
  /** Incident Commander fallback contact (used only when no Liaison Officer is stood up). */
  incident_commander_phone: z.string().nullable().optional(),
  incident_commander_email: z.string().nullable().optional(),
  /** Liaison Officer (App A #46) — the PRIMARY contact (duty line) when present. */
  liaison_officer_name: z.string().nullable().optional(),
  liaison_officer_phone: z.string().nullable().optional(),
  liaison_officer_email: z.string().nullable().optional(),
  /** The incident's operator-assessed Response Level (§5) + attribution (the IC's pin). */
  response_level: z.number().int().min(1).max(4).nullable().optional(),
  response_level_by: z.string().nullable().optional(),
  response_level_at: z.string().nullable().optional(),
  /** Declared state (statutory — distinct from Response Level; §5.4). NULL when not declared. */
  declared_authority_kind: z.enum(['municipal', 'first_nation', 'provincial']).nullable().optional(),
  declaration_number: z.string().nullable().optional(),
  /** Unified command (§4.7) — one shared command; co_leads carries the full roster. */
  unified: z.boolean().nullable().optional(),
  co_leads: z.array(OwnedIncidentCoLeadSchema).nullable().optional(),
  /** A pending §4.8 transfer / §4.7 unified invite — calling the outgoing IC mid-handover wastes 20 min. */
  pending_transfer_to: z.string().nullable().optional(),
  pending_transfer_kind: z.enum(['transfer', 'unified_invite']).nullable().optional(),
  /**
   * SERVER-COMPUTED viewer-relative flag — is the ACTING agency (req.operator.agencyId) a live
   * command participant? Drives the map cue's mine-vs-theirs inner mark. Computed server-side
   * because under AUTH-off the client has no agency id (the acting agency lives only on the server).
   */
  viewer_is_lead: z.boolean().nullable().optional(),
});
export type OwnedIncidentRef = z.infer<typeof OwnedIncidentRefSchema>;

/** IMS Response Level (Ontario IMS §5 Table 1): 1 Small · 2 Large · 3 Major ·
 *  4 Emergency. Persisted NOT NULL on high_risk_hotspots; drives the operator-map
 *  marker colour + size (E.2.3). */
export const ResponseLevelSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);
export type ResponseLevel = z.infer<typeof ResponseLevelSchema>;

export const HighRiskHotspotPropertiesSchema = z.object({
  id: z.string().uuid(),
  confidence: z.number().int().min(0).max(100),
  response_level: ResponseLevelSchema,
  observed_at: z.string(),
  corroborating_signals: HighRiskSignalSummarySchema,
  primary_hotspot: PrimaryHotspotSchema,
  // Unified Anomaly Queue working-state (migration 0058, #140) — present on the
  // queue list endpoint; optional so detail-constructed features stay valid.
  work_acknowledged_at: z.string().nullable().optional(),
  micro_status: z.enum(['new', 'working', 'resolving']).optional(),
  owner_label: z.string().nullable().optional(),
  // Coordination re-arch Phase 1 — the tracked incident this hotspot was Declared
  // into, if any (the alert↔incident thread). Optional so detail-constructed +
  // pre-existing-test features stay valid; null when not yet owned.
  owned_incident: OwnedIncidentRefSchema.nullable().optional(),
  // R1/#477 — the owning incident's lifecycle phase, which DRIVES the marker
  // transform (detected→confirmed→owned→managed→resolved). null when this hotspot is
  // not a tracked incident's source. Optional for back-compat with existing fixtures.
  incident_lifecycle_state: IncidentLifecycleStateSchema.nullable().optional(),
  // ── #445-ws2 — the owning incident's OPERATOR-ASSESSED Response Level overlay ──
  /** The IC's operator-assessed Response Level on the owning (non-closed) incident, or null
   *  when not owned / no pin. The queue converter reads the ESCALATION-SAFE max of this and
   *  the derived `response_level` (escalationSafeResponseLevel): an IC escalation lifts the
   *  queue row (COALESCE); an IC de-escalation can never bury a worse derived (the guard).
   *  The derived `response_level` is NEVER mutated. */
  operator_response_level: z.number().int().min(1).max(4).nullable().optional(),
  /** The assessing Incident Commander (command_officer_name snapshot), or null. */
  operator_rl_by: z.string().nullable().optional(),
  /** The IC's assessment reason for the pin, or null. */
  operator_rl_reason: z.string().nullable().optional(),
  /** When the IC's assessment landed (ISO), or null. */
  operator_rl_at: z.string().nullable().optional(),
});
export type HighRiskHotspotProperties = z.infer<typeof HighRiskHotspotPropertiesSchema>;

export const HighRiskHotspotFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: HighRiskHotspotPropertiesSchema,
});
export type HighRiskHotspotFeature = z.infer<typeof HighRiskHotspotFeatureSchema>;

export const HighRiskHotspotFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(HighRiskHotspotFeatureSchema),
});
export type HighRiskHotspotFeatureCollection = z.infer<
  typeof HighRiskHotspotFeatureCollectionSchema
>;

/** Per-component score breakdown persisted in the full signals payload. */
export const ScoreBreakdownSchema = z.object({
  base: z.number(),
  satellite: z.number(),
  fwi: z.number(),
  aqhi: z.number(),
  purpleair: z.number(),
  hms: z.number(),
  active_fire: z.number(),
  new_area: z.number(),
  total: z.number(),
  capped: z.boolean(),
});

/**
 * #442 Track A Slice 2 — the genuineness-gate verdict (static-industrial-heat mask).
 * Present on a fused hotspot's full signals ONLY when it sits inside a documented
 * static source. `demoted` = it was held off the urgent surface (response_level ≤ 2)
 * as an industrial heat source, not a fire; `frp_surge_override` = a real fire AT the
 * site kept it urgent. Lets the operator/Situation Unit DEFEND "demoted, not a fire".
 */
export const GenuinenessVerdictSchema = z.object({
  static_masked: z.boolean(),
  static_mask_source: z
    .object({ name: z.string(), source_type: z.string() })
    .nullable()
    .optional(),
  frp_floor_cleared: z.boolean(),
  frp_surge_override: z.boolean(),
  baseline_frp: z.number().nullable().optional(),
  // #447 Track A Slice 3b — the cluster is DIRECTIONALLY attached to a reported (known)
  // fire as its downwind spread, so its own urgent alert is suppressed (the known fire IS
  // the alert). `fire_id` is the UUID click/fly-to target; `fire_name` the operator label.
  // Absent/null = not connected (a separate/potentially-new fire — stays urgent-eligible).
  connected_to_fire: z
    .object({ fire_id: z.string(), fire_name: z.string() })
    .nullable()
    .optional(),
  // #447 — persistence look count over the cluster-key 24 h group (GOES-collapsed).
  // INFORMATIONAL in 3b (the persistence demotion is a soft corroborator deferred to 3c).
  persistence_looks: z.number().nullable().optional(),
  demoted: z.boolean(),
  // #447 — the NAMED demotion cause (a discriminator, not a bare union — Task #90).
  // 'static_heat' = Slice-2 industrial mask; 'connected_fire' = the directional attach.
  demoted_reason: z.enum(['static_heat', 'connected_fire']).nullable().optional(),
  // #448 Track A Slice 3c — the values-at-risk VETO kept this hotspot urgent (a KEEP, never
  // a demotion: `demoted` stays false). `nearest_place` mirrors the OC threatened-community
  // shape; `downwind` = the community is in the fire's wind path (the evacuation-risk lift).
  values_kept_urgent: z.boolean().optional(),
  nearest_place: z
    .object({
      name: z.string(),
      kind: z.string(),
      province: z.string().nullable(),
      distance_m: z.number(),
      bearing_deg: z.number().nullable(),
      downwind: z.boolean(),
    })
    .nullable()
    .optional(),
});
export type GenuinenessVerdict = z.infer<typeof GenuinenessVerdictSchema>;

/** The FULL corroborating_signals payload returned by the detail endpoint. */
export const HighRiskFullSignalsSchema = z.object({
  satellite_corroboration: z.boolean(),
  fwi: z.number().nullable(),
  aqhi_nearby: z.array(
    z.object({
      station_id: z.string(),
      aqhi_value: z.number(),
      distance_km: z.number(),
    }),
  ),
  purpleair_spike: z.array(
    z.object({
      sensor_id: z.string(),
      external_id: z.string(),
      pm2_5: z.number(),
      distance_km: z.number(),
    }),
  ),
  hms_overlap: z.boolean(),
  active_fire_nearby_id: z.string().nullable(),
  new_area_boost: z.boolean(),
  // #442 — the static-heat-mask genuineness verdict (absent unless masked).
  genuineness: GenuinenessVerdictSchema.nullable().optional(),
  score_breakdown: ScoreBreakdownSchema,
});

export const HighRiskHotspotDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: z.object({
    id: z.string().uuid(),
    confidence: z.number().int().min(0).max(100),
    response_level: ResponseLevelSchema,
    observed_at: z.string(),
    expires_at: z.string(),
    computed_at: z.string(),
    corroborating_signals: HighRiskFullSignalsSchema.nullable(),
    corroborating_hotspots: z.array(z.string()),
    primary_hotspot: PrimaryHotspotSchema,
    // Coordination re-arch Phase 1 — the owned-incident thread (see properties above).
    owned_incident: OwnedIncidentRefSchema.nullable().optional(),
  }),
});
export type HighRiskHotspotDetail = z.infer<typeof HighRiskHotspotDetailSchema>;
